'use client';

import React, { forwardRef, ForwardedRef, useId } from 'react';
import classNames from 'classnames';

import { Helper } from '../Helper';
import { Error } from '../Error';
import { RadioProps } from '../Radio/Radio';
import './css/RadioGroup.scss';

export interface RadioGroupProps extends React.HTMLAttributes<HTMLElement> {
  disabled?: boolean;
  error?: string;
  helper?: string;
  legend?: string;
  name?: string;
  required?: boolean;
  isErrorAnnounced?: boolean;
}

export const RadioGroup = forwardRef(
  (props: RadioGroupProps, ref: ForwardedRef<HTMLFieldSetElement>) => {
    const {
      className,
      disabled,
      error,
      helper,
      legend,
      name: nameProp,
      required,
      isErrorAnnounced = true,
      children,
      ...spreadProps
    } = props;
    const customRadioGroupId = `${useId()}-radio-group`;
    // assigning `name` value here instead of above in the destructuring to handle the `name=""` case]
    const name = nameProp || customRadioGroupId;
    const errorId = error ? `${name}-error` : '';
    const helperId = helper ? `${name}-helper` : '';
    const ariaDescribedByValue = `${errorId} ${helperId}`.trim() || null;

    return (
      <fieldset
        {...spreadProps}
        className={classNames({
          'ps-radio-group': true,
          'ps-radio-group-error': Boolean(error),
          [`${className}`]: className,
        })}
        disabled={disabled}
        ref={ref}
      >
        <legend>{legend}</legend>

        {helper && <Helper id={helperId}>{helper}</Helper>}

        {React.Children.map(children, (child) => {
          if (!React.isValidElement(child)) {
            return child;
          }
          return React.cloneElement(child, {
            // @ts-expect-error - ts is not recognizing this attribute on the child element
            'aria-describedby': ariaDescribedByValue,
            'data-error': error ? 'true' : null,
            name,
            'disabled': disabled || (child.props as RadioProps).disabled,
            'required': required || (child.props as RadioProps).required,
          });
        })}

        {error && (
          <Error
            id={errorId}
            isErrorAnnounced={isErrorAnnounced}
          >
            {error}
          </Error>
        )}
      </fieldset>
    );
  }
);
